import React from 'react';
import { Languages } from 'lucide-react';
import { LANGUAGES, useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * DE / EN / AL segmented toggle.
 * variant: 'compact' (header bar) | 'block' (mobile drawer)
 */
export const LanguageSwitcher = ({ variant = 'compact', className }) => {
  const { lang, setLang, t } = useI18n();

  if (variant === 'block') {
    return (
      <div
        className={cn('flex items-center justify-between gap-3', className)}
        data-testid="language-switcher-mobile"
      >
        <span className="inline-flex items-center gap-2 text-[0.78rem] text-[color:var(--rmh-muted)]">
          <Languages className="h-3.5 w-3.5" aria-hidden="true" />
          {t.header.languageGroupLabel}
        </span>
        <div
          role="group"
          aria-label={t.header.languageGroupLabel}
          className="flex items-center gap-1 rounded-full border p-1"
          style={{
            backgroundColor: 'var(--rmh-surface)',
            borderColor: 'var(--rmh-border-strong)',
          }}
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              aria-pressed={lang === l.code}
              data-testid={`language-switcher-mobile-${l.code}-button`}
              className={cn(
                'inline-flex h-8 min-w-[3rem] items-center justify-center rounded-full px-3 text-[0.72rem] font-medium uppercase tracking-[0.14em]',
                'transition-[background-color,color] duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--rmh-accent)]',
                lang === l.code
                  ? 'text-[color:var(--rmh-canvas)]'
                  : 'text-[color:var(--rmh-muted)] hover:text-[color:var(--rmh-ink)]',
              )}
              style={{
                backgroundColor: lang === l.code ? 'var(--rmh-ink)' : 'transparent',
              }}
            >
              {l.shortLabel}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={t.header.languageGroupLabel}
      data-testid="language-switcher"
      className={cn(
        'flex items-center gap-0.5 rounded-full border p-[3px]',
        className,
      )}
      style={{
        backgroundColor: 'var(--rmh-surface)',
        borderColor: 'var(--rmh-border-strong)',
      }}
    >
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          aria-pressed={lang === l.code}
          title={l.label}
          data-testid={`language-switcher-${l.code}-button`}
          className={cn(
            'inline-flex h-7 items-center justify-center rounded-full px-2.5 text-[0.68rem] font-medium uppercase tracking-[0.14em]',
            'transition-[background-color,color] duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--rmh-accent)]',
            lang === l.code
              ? 'text-[color:var(--rmh-canvas)]'
              : 'text-[color:var(--rmh-muted)] hover:text-[color:var(--rmh-ink)]',
          )}
          style={{
            backgroundColor: lang === l.code ? 'var(--rmh-ink)' : 'transparent',
          }}
        >
          {l.shortLabel}
        </button>
      ))}
    </div>
  );
};

export default LanguageSwitcher;
